import { useState, useCallback, useMemo } from 'react';
import { useStore } from '../../context/StoreContext';
import useMetrics from '../../hooks/useMetrics';
import { functionsBaseUrl } from '../../firebase/firebase';
import AnalyticsPinGate from './AnalyticsPinGate';
import DateRangeFilter from './DateRangeFilter';
import MetricsSummary from './MetricsSummary';
import OrderTrendsChart, { TotalOrdersChart } from './OrderTrendsChart';
import DayOfWeekChart from './DayOfWeekChart';
import ProductMixChart from './ProductMixChart';
import ChannelMixChart from './ChannelMixChart';
import AnalyticsFAQ from './AnalyticsFAQ';
import hotZoneService from '../../services/hotzone.service';
import './Analytics.css';

/**
 * Info icon component with tooltip
 */
function InfoButton({ tooltip, id }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipId = id || `info-tooltip-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <span
      className="info-button"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(!showTooltip)}
      onKeyDown={(e) => e.key === 'Enter' && setShowTooltip(!showTooltip)}
      role="button"
      tabIndex={0}
      aria-label="Más información"
      aria-describedby={showTooltip ? tooltipId : undefined}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 14.5a6.5 6.5 0 110-13 6.5 6.5 0 010 13zM7 7h2v5H7V7zm0-3h2v2H7V4z"/>
      </svg>
      {showTooltip && (
        <span className="info-tooltip" id={tooltipId} role="tooltip">{tooltip}</span>
      )}
      {/* Screen reader accessible description (always available) */}
      <span className="sr-only">{tooltip}</span>
    </span>
  );
}

/**
 * Main Analytics Dashboard component
 * Shows order trends and metrics for the selected store and date range
 */
export default function Analytics() {
  return (
    <AnalyticsPinGate>
      <AnalyticsDashboard />
    </AnalyticsPinGate>
  );
}

function AnalyticsDashboard() {
  const { activeStoreId, canSwitchStores } = useStore();
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);

  // For owners who can see all stores, default to 'all'
  // For managers, use their assigned store
  const metricsStoreId = canSwitchStores ? activeStoreId || 'all' : activeStoreId;

  /**
   * Run channel data backfill for historical orders
   */
  const runChannelBackfill = useCallback(async () => {
    if (!window.confirm('¿Ejecutar backfill de datos de canal para órdenes históricas? Esto puede tomar unos minutos.')) {
      return;
    }

    setBackfillLoading(true);
    setBackfillStatus(null);

    try {
      const response = await fetch(`${functionsBaseUrl}/backfillChannelMetrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (response.ok) {
        setBackfillStatus({
          success: true,
          message: `Backfill completado: ${result.metricsDocuments} documentos actualizados`,
          details: result,
        });
      } else {
        setBackfillStatus({
          success: false,
          message: `Error: ${result.error || 'Error desconocido'}`,
        });
      }
    } catch (error) {
      setBackfillStatus({
        success: false,
        message: `Error de conexión: ${error.message}`,
      });
    } finally {
      setBackfillLoading(false);
    }
  }, []);

  const {
    loading,
    error,
    chartData,
    summary,
    previousSummary,
    tickInterval,
    dayOfWeekData,
    productMix,
    channelMix,
    comparisonLabels,
    dateRange,
    presets,
    currentDateRange,
    selectPreset,
    selectCustomRange,
    refresh,
  } = useMetrics(metricsStoreId);

  // Hot zone detection
  const spikes = useMemo(
    () => (chartData ? hotZoneService.detectSpikes(chartData) : []),
    [chartData]
  );

  const spikeSet = useMemo(
    () => new Set(spikes.map((s) => s.date)),
    [spikes]
  );

  const peakPeriods = useMemo(
    () => (chartData ? hotZoneService.getRankedPeakPeriods(chartData, 5) : []),
    [chartData]
  );

  const upcomingAlerts = useMemo(
    () => hotZoneService.getUpcomingHolidayAlerts(new Date(), chartData || []),
    [chartData]
  );

  return (
    <div className="basicContainer analytics-container">
      <div className="analytics-wrapper">
        {/* Upcoming holiday alerts */}
        {upcomingAlerts.length > 0 && (
          <div className="holiday-alerts">
            {upcomingAlerts.map((alert) => (
              <div key={alert.holidayId} className="holiday-alert">
                <span className="holiday-alert-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </span>
                <span className="holiday-alert-text">
                  {alert.message}{alert.lastYearOrders > 0 ? ' (basado en el ano pasado)' : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Safe browsing banner */}
        <div className="safe-banner">
          <span className="safe-banner-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
            </svg>
          </span>
          <span className="safe-banner-text">
            <strong>Solo lectura</strong> — Haz clic donde quieras, no se borra ni cambia nada.
          </span>
        </div>

        <header className="analytics-header">
          <div className="analytics-title-section">
            <h1>Analytics</h1>
            <p className="analytics-subtitle">
              Tendencias de órdenes{' '}
              {metricsStoreId === 'all'
                ? '(Todas las tiendas)'
                : `(${metricsStoreId})`}
            </p>
          </div>
          <div className="analytics-actions">
            <button className="refresh-btn" onClick={refresh} disabled={loading}>
              {loading ? 'Cargando...' : 'Actualizar'}
            </button>
            <InfoButton tooltip="Carga los números más recientes" />
          </div>
        </header>

        <div className="filter-section">
          <div className="section-header">
            <span className="section-label">Fechas</span>
            <InfoButton tooltip="Elige qué días quieres ver" />
          </div>
          <DateRangeFilter
            dateRange={dateRange}
            presets={presets}
            onSelectPreset={selectPreset}
            onSelectCustomRange={selectCustomRange}
          />
        </div>

        {error && (
          <div className="analytics-error">
            <p>{error}</p>
            <button onClick={refresh}>Reintentar</button>
          </div>
        )}

        {loading && (
          <div className="analytics-loading">
            <div className="loading-spinner"></div>
            <p>Cargando métricas...</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <MetricsSummary
              summary={summary}
              previousSummary={previousSummary}
              dateRangeLabel={currentDateRange.label}
              comparisonLabels={comparisonLabels}
            />

            {/* Two-column grid for smaller charts */}
            <div className="analytics-grid">
              <DayOfWeekChart data={dayOfWeekData} />
              <ProductMixChart data={productMix} totalOrders={summary?.totalOrders} />
              <ChannelMixChart data={channelMix} />
            </div>

            <div className="charts-section">
              <TotalOrdersChart chartData={chartData} tickInterval={tickInterval} spikeSet={spikeSet} />
              <OrderTrendsChart
                chartData={chartData}
                tickInterval={tickInterval}
                title="Órdenes por Tipo"
              />
            </div>

            {/* Peak periods section */}
            {peakPeriods.length > 0 && (
              <div className="peak-periods-section">
                <h3>Periodos Pico</h3>
                <p className="metric-subtitle" style={{ marginBottom: '1rem' }}>
                  Dias con 1.5x+ del promedio, agrupados si son consecutivos. Ordenados por volumen.
                </p>
                <div className="peak-periods-list">
                  {peakPeriods.map((period, i) => (
                    <div key={i} className="peak-period-card">
                      <div className="peak-period-rank">#{i + 1}</div>
                      <div className="peak-period-info">
                        <div className="peak-period-label">{period.label}</div>
                        <div className="peak-period-dates">
                          {period.startDate === period.endDate
                            ? period.startDate
                            : `${period.startDate} — ${period.endDate}`}
                        </div>
                      </div>
                      <div className="peak-period-stats">
                        <span className="peak-period-total">{period.totalOrders}</span>
                        <span className="peak-period-unit">pedidos</span>
                        {period.avgMultiplier > 1 && (
                          <span className="peak-period-multiplier">
                            {period.avgMultiplier}x
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <AnalyticsFAQ />

            {/* Admin section - only for owners */}
            {canSwitchStores && (
              <div className="admin-section">
                <h3>Herramientas de Admin</h3>
                <p className="admin-description">
                  Si los datos de canal (En Línea / En Tienda) no aparecen para fechas anteriores,
                  ejecuta el backfill para procesar órdenes históricas.
                </p>
                <button
                  className="backfill-btn"
                  onClick={runChannelBackfill}
                  disabled={backfillLoading}
                >
                  {backfillLoading ? 'Procesando...' : 'Backfill Datos de Canal'}
                </button>
                {backfillStatus && (
                  <div className={`backfill-status ${backfillStatus.success ? 'success' : 'error'}`}>
                    {backfillStatus.message}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
